// SS_Ensemble Client Application - Socket.IO Management

import { state } from './state.js';
import { updateSyncIndicator, showReconnectingBanner, hideReconnectingBanner } from './ui.js';

// Type definitions for Socket.IO client (global from CDN)
declare const io: any;

export const socket = io({
    path: '/museiq/socket.io',
    // Better reconnection settings for debugging
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionAttempts: Infinity,
    timeout: 20000,
    // Don't disconnect on browser tab switching
    autoConnect: true,
    forceNew: false
});

// Socket ID the server last received an 'identify' for. A reconnect gives the
// socket a new ID, which triggers a fresh identify on the next event.
let identifiedSocketId: string | undefined | null = null;

/**
 * Tell the server which signed-in user owns this socket, once per socket ID.
 * Call before emitting client events: the server uses it to log them even when
 * the conductor has no server session. socket.io keeps event order on a
 * socket (and buffers while disconnected), so identify arrives first.
 */
export function ensureSocketIdentified(): void {
    if (!state.authToken) return;
    if (identifiedSocketId !== null && socket.id === identifiedSocketId) return;
    socket.emit('identify', { authToken: state.authToken });
    identifiedSocketId = socket.id;
}

// Callbacks to run when the socket connects again after a drop (new socket ID).
const reconnectCallbacks: Array<() => void> = [];
let hasConnectedOnce = false;

/**
 * Run `cb` every time the socket reconnects after a drop. Not called for the
 * first connection at page load — app init handles that (claimRole).
 */
export function onSocketReconnected(cb: () => void): void {
    reconnectCallbacks.push(cb);
}

// Socket connection handling
export function initializeSocketHandlers(): void {
    // The first connection may already be up by the time this runs.
    if (socket.connected) hasConnectedOnce = true;

    socket.on('connect', () => {
        console.log('[Socket] Connected to server, socketId:', socket.id);
        // Update UI to show connected state
        updateSyncIndicator('active');
        hideReconnectingBanner();

        if (!hasConnectedOnce) {
            hasConnectedOnce = true;
            return;
        }
        console.log('[Socket] Reconnected. Session state:', {
            sessionId: state.sessionId,
            role: state.role,
            hasToken: !!state.conductorToken,
            socketId: socket.id
        });
        for (const cb of reconnectCallbacks) cb();
    });

    socket.on('disconnect', (reason: string) => {
        console.log('[Socket] Disconnected:', reason);
        console.log('[Socket] Session state at disconnect:', {
            sessionId: state.sessionId,
            role: state.role,
            hasToken: !!state.conductorToken,
            socketId: socket.id
        });
        updateSyncIndicator('inactive');

        // Show reconnecting banner for unexpected disconnections
        if (state.role && reason !== 'io server disconnect') {
            console.warn('[Socket] Connection lost, attempting to reconnect...');
            showReconnectingBanner();
        }
    });

    // Reconnection events live on the Manager (socket.io) in socket.io v4,
    // not on the socket itself.
    socket.io.on('reconnect_error', (error: any) => {
        console.log('[Socket] Reconnection attempt failed:', error);
        updateSyncIndicator('inactive');
    });

    socket.io.on('reconnect_failed', () => {
        console.log('[Socket] All reconnection attempts failed');
        updateSyncIndicator('inactive');
        if (state.role) {
            alert('Connection lost and could not be restored. Please reload the page.');
        }
    });
}
